import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BrutalCard } from "@/components/Brutal";
import api, { formatError } from "@/lib/api";
import { BellRing, Mail, MessageSquare, Moon, Loader2, ChevronDown } from "lucide-react";

const CHANNEL = {
  push: { label: "Push", Icon: BellRing, cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  email: { label: "E-mail", Icon: Mail, cls: "text-sky-700 bg-sky-50 border-sky-200" },
  sms: { label: "SMS", Icon: MessageSquare, cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
const TOPICS = [
  { key: "assigned", label: "Toegewezen taken" },
  { key: "reminders", label: "Herinneringen" },
  { key: "digest", label: "Dagsamenvatting" },
  { key: "announcements", label: "Mededelingen" },
  { key: "polls", label: "Polls" },
  { key: "badges", label: "Badges & punten" },
  { key: "birthdays", label: "Verjaardagen" },
];

const ChannelPill = ({ channel }) => {
  const c = CHANNEL[channel] || CHANNEL.push;
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border ${c.cls}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />{c.label}
    </span>
  );
};

// Herbruikbare bediening: hoofdkanalen + per-meldingssoort (aan/uit + kanaal)
export const MemberNotifControls = ({ m, onChanged }) => {
  const [saving, setSaving] = useState(false);
  const channelOptions = ["push", "email", "sms"];
  const channels = m.primary_channels || (m.primary_channel ? [m.primary_channel] : ["push"]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/org/members-notif-prefs/${m.user_id}`, patch);
      onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const setPrimaryChannels = (chs) => save({ primary_channels: chs.length ? chs : ["push"] });
  const toggleChannel = (ch) => {
    const next = channels.includes(ch) ? channels.filter((c) => c !== ch) : [...channels, ch];
    setPrimaryChannels(next.length ? next : [ch]);
  };
  const setTopicOn = (key, on) => save({ triggers: { [key]: on } });

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-xs font-bold text-brand-ink/50 w-28 shrink-0 pt-1.5 flex items-center gap-1">Kanalen {saving && <Loader2 className="w-3 h-3 animate-spin" />}</span>
        <div className="flex flex-wrap gap-1.5" data-testid={`member-primary-${m.user_id}`}>
          {channelOptions.map((c) => {
            const active = channels.includes(c);
            const { Icon } = CHANNEL[c];
            return (
              <button key={c} onClick={() => toggleChannel(c)} data-testid={`member-channel-${m.user_id}-${c}`}
                className={`inline-flex items-center gap-1 text-xs font-bold rounded-full px-2.5 py-1 border transition-colors ${active ? CHANNEL[c].cls : "text-brand-ink/40 bg-white border-brand-ink/15"}`}>
                <Icon className="w-3 h-3" strokeWidth={2.5} />{CHANNEL[c].label}
                {active && <span className="ml-0.5">✓</span>}
              </button>
            );
          })}
        </div>
        {channels.includes("sms") && m.phone_masked && <span className="text-xs text-brand-ink/50 pt-1.5">({m.phone_masked})</span>}
      </div>

      <div className="border-t border-dashed border-brand-ink/10 pt-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => save({ quiet_enabled: !m.quiet_enabled })} data-testid={`member-quiet-toggle-${m.user_id}`}
            className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${m.quiet_enabled ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
            <span className="w-4 h-4 rounded-full bg-white brutal-sm" />
          </button>
          <span className="text-sm font-bold flex items-center gap-1.5 flex-1 min-w-0"><Moon className="w-3.5 h-3.5" strokeWidth={2.5} />Stille uren</span>
          {m.quiet_enabled && (
            <div className="flex items-center gap-1.5">
              <input type="time" defaultValue={m.quiet_start || "22:00"} onChange={(e) => e.target.value && save({ quiet_start: e.target.value })}
                data-testid={`member-quiet-start-${m.user_id}`} className="brutal-sm rounded-lg px-2 py-1 text-xs font-medium outline-none bg-white" />
              <span className="text-xs font-bold text-brand-ink/40">–</span>
              <input type="time" defaultValue={m.quiet_end || "07:00"} onChange={(e) => e.target.value && save({ quiet_end: e.target.value })}
                data-testid={`member-quiet-end-${m.user_id}`} className="brutal-sm rounded-lg px-2 py-1 text-xs font-medium outline-none bg-white" />
            </div>
          )}
        </div>
        {m.quiet_enabled && <p className="text-[11px] text-brand-ink/40 mt-1 ml-11">Meldingen worden tussen deze tijden stilgehouden.</p>}
      </div>

      <div className="border-t border-dashed border-brand-ink/10 pt-2">
        <p className="text-xs font-bold text-brand-ink/50 mb-1.5">Per meldingssoort <span className="font-semibold text-brand-ink/40">— kies één of meer kanalen</span></p>
        <div className="space-y-1.5">
          {TOPICS.map((tp) => {
            const on = m.triggers?.[tp.key] !== false;
            const rawOv = (m.channel_overrides || {})[tp.key];
            const ovList = Array.isArray(rawOv) ? rawOv : (typeof rawOv === "string" && rawOv ? [rawOv] : []);
            const isDefault = ovList.length === 0;
            const setOv = (list) => save({ channel_overrides: { [tp.key]: list } });
            const toggleOv = (c) => setOv(ovList.includes(c) ? ovList.filter((x) => x !== c) : [...ovList, c]);
            return (
              <div key={tp.key} className="rounded-lg bg-white/70 brutal-sm px-2 py-1.5" data-testid={`member-topic-${m.user_id}-${tp.key}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => setTopicOn(tp.key, !on)} data-testid={`member-topic-toggle-${m.user_id}-${tp.key}`}
                    className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${on ? "bg-brand-green justify-end" : "bg-brand-ink/20 justify-start"}`}>
                    <span className="w-4 h-4 rounded-full bg-white brutal-sm" />
                  </button>
                  <span className={`text-sm font-bold flex-1 min-w-0 truncate ${on ? "" : "text-brand-ink/30 line-through"}`}>{tp.label}</span>
                </div>
                {on && (
                  <div className="flex flex-wrap gap-1 mt-1.5 pl-11" data-testid={`member-topic-channels-${m.user_id}-${tp.key}`}>
                    <button onClick={() => setOv([])} data-testid={`member-topic-default-${m.user_id}-${tp.key}`}
                      className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border transition-colors ${isDefault ? "text-indigo-700 bg-indigo-50 border-indigo-200" : "text-brand-ink/40 bg-white border-brand-ink/15"}`}>
                      Standaard{isDefault ? " ✓" : ""}
                    </button>
                    {channelOptions.map((c) => {
                      const active = ovList.includes(c);
                      const { Icon } = CHANNEL[c];
                      return (
                        <button key={c} onClick={() => toggleOv(c)} data-testid={`member-topic-ch-${m.user_id}-${tp.key}-${c}`}
                          className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border transition-colors ${active ? CHANNEL[c].cls : "text-brand-ink/40 bg-white border-brand-ink/15"}`}>
                          <Icon className="w-3 h-3" strokeWidth={2.5} />{CHANNEL[c].label}{active ? " ✓" : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-brand-ink/40 mt-2">"Standaard" volgt de gekozen hoofdkanalen hierboven. Kies één of meer kanalen om per soort af te wijken.</p>
      </div>
    </div>
  );
};

const MemberRow = ({ m, onChanged }) => {
  const [open, setOpen] = useState(false);
  const overrides = Object.entries(m.channel_overrides || {}).filter(([, v]) => v);
  const channels = m.primary_channels || (m.primary_channel ? [m.primary_channel] : ["push"]);

  return (
    <div className="py-3" data-testid={`member-notif-${m.user_id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 text-left">
        {m.avatar_url
          ? <img src={m.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
          : <div className="w-9 h-9 rounded-full bg-brand-yellow/60 flex items-center justify-center font-head font-black shrink-0">{m.name?.[0]?.toUpperCase()}</div>}
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm truncate">{m.name} {m.role === "beheerder" && <span className="text-[10px] font-bold text-indigo-500">· beheerder</span>}</p>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {channels.map((ch) => <ChannelPill key={ch} channel={ch} />)}
            {m.quiet_enabled && <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 border text-slate-600 bg-slate-100 border-slate-200"><Moon className="w-3 h-3" />{m.quiet_start}–{m.quiet_end}</span>}
            {overrides.length > 0 && <span className="text-[11px] font-bold text-slate-400">+{overrides.length} afwijkend</span>}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-brand-ink/40 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 ml-1 sm:ml-12">
          <MemberNotifControls m={m} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
};

// Editor voor één lid (gebruikt in de leden-dialoog)
export const MemberNotifEditor = ({ userId }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/org/members-notif-prefs").then((r) => setData(r.data)).catch(() => setData({ members: [] }));
  useEffect(() => { load(); }, [userId]);
  if (!data) return <div className="flex items-center gap-2 text-brand-ink/40 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Laden…</div>;
  const m = (data.members || []).find((x) => x.user_id === userId);
  if (!m) return <p className="text-sm text-brand-ink/40 py-2">Geen meldingsvoorkeuren gevonden.</p>;
  return <MemberNotifControls m={m} onChanged={load} />;
};

export const MemberNotifPrefs = () => {
  const [data, setData] = useState(null);
  const load = () => api.get("/org/members-notif-prefs").then((r) => setData(r.data)).catch(() => setData({ members: [] }));
  useEffect(() => { load(); }, []);
  return (
    <BrutalCard data-testid="member-notif-prefs-card">
      <p className="font-head font-bold flex items-center gap-2"><BellRing className="w-4 h-4" strokeWidth={2.5} />Meldingen per lid</p>
      <p className="text-xs text-brand-ink/50 mt-0.5">Stel per huisgenoot het meldingskanaal in — ook per meldingssoort (aan/uit + kanaal).</p>
      {!data ? (
        <div className="flex items-center gap-2 text-brand-ink/40 py-6"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>
      ) : (data.members || []).length === 0 ? (
        <p className="text-sm text-brand-ink/40 py-4">Geen leden gevonden.</p>
      ) : (
        <div className="mt-2 divide-y divide-brand-ink/10">
          {data.members.map((m) => <MemberRow key={m.user_id} m={m} onChanged={load} />)}
        </div>
      )}
    </BrutalCard>
  );
};
